<script lang="ts" setup>
import {
  ComputedRef,
  PropType,
  computed,
  inject,
  nextTick,
  onBeforeMount,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from 'vue';
import {
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirPmDraggableSorting,
  DragModel,
  TirPmScrollbar,
  TirPmPopover,
  PopoverPositionEnum,
  TirPmIconView,
  TirPmNotificationModel,
  TirPmNotificationEventBus,
} from 'tir-components';
import {
  PlusIcon,
  EllipsisHorizontalIcon,
  FolderOpenIcon,
  ListBulletIcon,
  MagnifyingGlassIcon,
} from 'tir-style-system/icons/outline';
import {
  TasksKanbanColumnModel,
  TaskModel,
  TasksKanbanGroupingModel,
  TaskStatusModel,
} from './models';
import { useKanbanManager, useTasksKanbanDraggble } from './composables';
import {
  TaskKanbanCard,
  TasksKanbanFilter,
  KanbanLayout,
  KanbanColumn,
  KanbanDraggableSorting,
  TasksKanbanColumnIndicator,
} from './components';
import { TaskViewSettings, TasksKanbanExternalSettings } from './types';
import { TasksKanbanGrouppingTypeEnum, TaskViewElementEnum } from './enums';
import {
  TaskDrawer,
  TaskDrawerSettingsModel,
  TaskDrawerFormDataModel,
  TaskDrawerTaskModel,
  RemoveTaskModal,
} from '@/components';
import { useRouter } from 'vue-router';

import { TaskKanbanAdapterService } from './adapter';
import TirPmSystemMessage from 'tir-pm-system-message/src/tir-pm-system-message.vue';
import { DimondIcon } from 'tir-style-system/icons/solid';
import { CurrentUser, UserRoleEnum } from 'tir-pm-user-core';
import ProjectQueueTaskListItem from '@/api/Main/dataSource/api/projects/groups/i-service/model/queue-tasks/ProjectQueueTaskListItem';
import SubscribeToQueueTasksRequest from './models/SubscribeToQueueTasksRequest';
import ListenTaskInfoAdapter from './adapter/ListenTaskInfoAdapter';
import TaskKanbanSkeleton from './components/TaskKanbanSkeleton.vue';
import { MiddlewareAdapterService } from '@/router/middleware';
import { AttributesLocalStorageService } from '@/services';
import GetAccessCurrentUserInQueueTaskByIdRequestModel from './models/GetAccessCurrentUserInQueueTaskByIdRequestModel';
import GetAccessCurrentUserInQueueTaskByIdResponseModel from './models/GetAccessCurrentUserInQueueTaskByIdResponseModel';

const router = useRouter();
/**Сервис проверки доступов */
const middlewareService = new MiddlewareAdapterService();
const apiService = new TaskKanbanAdapterService();

const emit = defineEmits<{
  /**Событие для раскрытия сайтбара */
  (e: 'toggle-submenu', queueId: string): void;
  (e: 'open-options', queueId: string): void;
}>();

const props = defineProps({
  /**Настройки */
  settings: {
    type: Object as PropType<TasksKanbanExternalSettings>,
    default: (): TasksKanbanExternalSettings => ({
      showNestedTasks: true,
    }),
  },
  /**ID очереди */
  queueId: {
    type: String,
    required: true,
  },
  /**ID проекта */
  projectId: {
    type: Number,
    required: true,
  },
  /**ID неймспейса */
  namespaceId: {
    type: String,
    required: true,
  },
});
/**Информация проекта */
const project = inject('project');
/**Ссылка на компонент шторки задачи */
const $taskDrawer = ref<InstanceType<typeof TaskDrawer>>();
/**Окно удаления задачи */
const $removeTaskModal = ref<InstanceType<typeof RemoveTaskModal>>();

const adapterListen = new ListenTaskInfoAdapter({
  CreateTasksEventFn: updateTask,
  RemoveTaskEventFn: updateTask,
});

const currentQueue = inject('currentQueue') as ComputedRef<ProjectQueueTaskListItem>;
const isCanAddTask = inject('isCanAddTask') as ComputedRef<Boolean>;
/**Индекс колонки, которую можно перетаскивать */
const allowDragColumnIndex = ref<number>();
/**Является ли пользователь админом проекта */
const isProjectAdmin = ref(false);
/**Состояние сброса поиска */
const resetSearchState = ref();
/**Была ли выбрана группировка */
const selectGrouppingState = ref();
/**index колонки */
const columnIndex = ref<number>();
/**Доступы юзера к задаче */
const userAccess = ref<GetAccessCurrentUserInQueueTaskByIdResponseModel>();

/**Переносится ли задача */
const isTransferTask = computed<boolean>(
  () => router.currentRoute.value?.query.isTransferTask == 'true',
);
/**Не передвигаемые группировки */
const nonDraggableGroups = computed<boolean>(() => {
  return (
    grouppingType.value === TasksKanbanGrouppingTypeEnum.Performer ||
    grouppingType.value === TasksKanbanGrouppingTypeEnum.Tag
  );
});

/**
 * * Открыть создание задачи
 * @param column Колонка
 */
const openCreateTask = (column?: TasksKanbanColumnModel) => {
  const groupping = column?.Grouping;
  $taskDrawer.value?.open(
    new TaskDrawerSettingsModel({
      Mode: 'create',
      ProjectId: props.projectId,
      QueueId: props.queueId,
      NamespaceId: props.namespaceId,
      FormData: new TaskDrawerFormDataModel({
        StatusId: groupping?.Status?.Id,
        PriorityId: groupping?.Priority?.Id,
        TypeId: groupping?.Type?.Id,
        PerformerIds: groupping?.Performer?.Id ? [groupping.Performer.Id] : [],
        Tags: groupping?.Tag?.Id ? [groupping.Tag.Id] : [],
      }),
    }),
  );
};

/**Открыть задачу */
const openTaskInDrawer = (taskId: number) => {
  $taskDrawer.value?.open(
    new TaskDrawerSettingsModel({
      Mode: 'edit',
      TaskId: taskId,
    }),
  );
};

/**Обновить драйвер задачи */
const onUpdateTaskDrawer = () => {
  if ('copy' in router.currentRoute.value?.query) {
    $taskDrawer.value?.close();
    checkTaskCopying();
  }
};

/**Обновить колонку с задачами */
const updateColumn = async (task: TaskDrawerTaskModel) => {
  const findColumn = columns.value.find((column) => {
    const groupping = column.Grouping;

    switch (grouppingType.value) {
      case TasksKanbanGrouppingTypeEnum.Status:
        return groupping.Status !== undefined && groupping.Status.Id === task.StatusId;
      case TasksKanbanGrouppingTypeEnum.Priority:
        return groupping.Priority !== undefined && groupping.Priority.Id === task.PriorityId;
      case TasksKanbanGrouppingTypeEnum.Author:
        return groupping.Author !== undefined && groupping.Author.Id === task.OwnerId;
      case TasksKanbanGrouppingTypeEnum.Performer:
        return (
          groupping.Performer !== undefined && task.PerformerIds.includes(groupping.Performer.Id)
        );
      case TasksKanbanGrouppingTypeEnum.Tag:
        return groupping.Tag !== undefined && task.Tags.includes(groupping.Tag.Id);
      case TasksKanbanGrouppingTypeEnum.Type:
        return groupping.Type !== undefined && task.TypeId === groupping.Type.Id;
    }
  });

  if (findColumn) updateTasksInColumn(findColumn);
  else loadAllGrouppedTasks(props.queueId);
};

/**Получение доступов по текущему пользователю в очереди задач по идентификатору */
const getAccessCurrentUserInQueueTask = async () => {
  const response = await apiService.getAccessCurrentUserInQueueTaskById(
    new GetAccessCurrentUserInQueueTaskByIdRequestModel({
      QueueTaskId: props.queueId,
    }),
  );
  if (response.IsSuccess) {
    userAccess.value = response.Value;
  }
};
/**При создании задачи */
const onCreateTask = (task: TaskDrawerTaskModel) => {
  updateColumn(task);
  loadAllGrouppedTasks(props.queueId);
};

/**При обновлении задачи */
const onUpdateTask = () => {
  loadAllGrouppedTasks(props.queueId);
};

function updateTask() {
  loadAllGrouppedTasks(props.queueId);
}
const removeSubtaskItem = () => {
  loadAllGrouppedTasks(props.queueId);
};

const taskViewSettings = computed<TaskViewSettings>(() => ({
  showNestedTasks: props.settings.showNestedTasks,
  viewElements: viewElements.value,
}));

const tasks = ref(new Array<TaskModel>());
const columns = ref(new Array<TasksKanbanColumnModel>());
const loading = ref();

const {
  grouppingType,
  viewElements,
  filter,
  isLoading,
  onScrollColumn,
  loadAllGrouppedTasks,
  loadSubtasks,
  moveTask,
  debounceLoadAllGrouppedTasks,
  updateTasksInColumn,
} = useKanbanManager({
  columns: columns,
  tasks: tasks,
  bottomScrollOffset: 200,
  firstRequestTakeCount: 10,
});

/**Кол-во задач */
const countTasks = computed(() => {
  return tasks.value.map((task) => task).length;
});
/** Флаг, указывающий, что пользователь не является админом НС/Овнером и задач для просмотра нет */
const isRestrictedFromViewingTasks = computed(() => {
  return !userAccess.value?.IsNamespaceAdmin && !userAccess.value?.IsOwner && !countTasks.value;
});
/**Текс для заглушки создания задачи */
const createTaskPlaceholderText = computed(() => {
  return isRestrictedFromViewingTasks.value
    ? 'В данной очереди тикеты для Вас отсутствуют'
    : 'В данной очереди еще нет задач';
});

const tasksIndexed = computed(() =>
  tasks.value.reduce<Record<number | string, TaskModel>>((acc, task) => {
    return {
      ...acc,
      [task.Id]: task,
    };
  }, {}),
);

/**Перетаскиваемые колонки */
const dragColumns = computed(() => {
  return columns.value.map(
    (c, i) =>
      new DragModel({
        Data: c,
        AllowDrag: allowDragColumnIndex.value == i,
        AllowDrop: true,
      }),
  );
});

watch(
  () => props.queueId,
  () => {
    onUpdateQueueId();
    configureCardsByGrouping();
  },
);

watch(() => router.currentRoute.value?.fullPath, onUpdateTaskDrawer);
/**Отслеживание изменения группировки */
watch(
  () => grouppingType.value,
  () => configureCardsByGrouping(),
);

/**Подключение к комнате очереди задачи */
const connectToTaskQueue = async () => {
  await apiService.subscribeToQueueEvent(
    new SubscribeToQueueTasksRequest({
      QueueId: String(router.currentRoute.value.params.queueId),
    }),
  );
};

onUnmounted(() => {
  apiService.udSubscribeToQueueEvent(
    new SubscribeToQueueTasksRequest({
      QueueId: String(router.currentRoute.value.params.queueId),
    }),
  );
});

onMounted(async () => {
  await getAccessCurrentUserInQueueTask();
  denterDataLocalStorage.value = AttributesLocalStorageService.getViewElements(props.queueId) ?? [];
  configureCardsByGrouping();
  apiService.ToggleSubscribe(true);
  onUpdateTaskDrawer();

  // apiService.UpdateQueueTaskStatusEvent.on(updateQueue);
});
onBeforeMount(async () => {
  apiService.ToggleSubscribe(false);

  await connectToTaskQueue();

  // apiService.UpdateQueueTaskStatusEvent.removeListener(updateQueue);
  apiService.UpdateQueueTaskStatusEvent.removeListener(onUpdateQueueId);
  // apiService.UpdateQueueTaskStatusEvent.removeListener(IsCanAddTask);
});

const formData = ref();

/**Добавить оповещение */
const addNotification = (message: string, isError: boolean = false, life?: number) => {
  TirPmNotificationEventBus.emit(
    'add',
    new TirPmNotificationModel({
      title: message,
      type: isError ? 'error' : 'success',
      group: 'general',
      life: life ?? 2000,
      closable: true,
    }),
  );
};

/**Проверка наличия коипрования задачи */
const checkTaskCopying = async () => {
  const copyTaskId = Number(router.currentRoute.value.query.copy);
  router.replace({ query: {} });

  if (copyTaskId && !isTransferTask.value) {
    const routeParams = router.currentRoute.value?.params;
    $taskDrawer.value?.open(
      new TaskDrawerSettingsModel({
        Mode: 'copy',
        TaskId: copyTaskId,
        ProjectId: Number(routeParams.projectId),
        QueueId: String(routeParams.queueId),
        NamespaceId: String(routeParams.namespaceId),
      }),
    );
  } else {
    await $taskDrawer.value?.getTransferredTaskData();

    router.push({
      name: 'task-detail',
      params: {
        taskId: $taskDrawer.value?.transferTaskId,
      },
    });
  }
};
/**Дефолтный вид карточек в зависимости от группировки */
function configureCardsByGrouping() {
  const dataView = AttributesLocalStorageService.getViewElements(props.queueId) ?? [];
  /**Часто используемые пункты */
  const frequentItems = [
    TaskViewElementEnum.Difficulty,
    TaskViewElementEnum.EstimateTime,
    TaskViewElementEnum.WorkTime,
    TaskViewElementEnum.Type,
  ];

  switch (grouppingType.value) {
    case TasksKanbanGrouppingTypeEnum.Status:
      viewElements.value = Array.from(
        new Set([
          ...dataView,
          TaskViewElementEnum.Performers,
          TaskViewElementEnum.Priority,
          ...frequentItems,
        ]),
      );
      break;
    case TasksKanbanGrouppingTypeEnum.Performer:
      viewElements.value = Array.from(
        new Set([
          ...dataView,
          TaskViewElementEnum.Status,
          TaskViewElementEnum.Priority,
          ...frequentItems,
        ]),
      );
      break;
    case TasksKanbanGrouppingTypeEnum.Priority:
      viewElements.value = Array.from(
        new Set([
          ...dataView,
          TaskViewElementEnum.Status,
          TaskViewElementEnum.Performers,
          ...frequentItems,
        ]),
      );
      break;
    case TasksKanbanGrouppingTypeEnum.Tag:
      viewElements.value = Array.from(
        new Set([
          ...dataView,
          TaskViewElementEnum.Status,
          TaskViewElementEnum.Performers,
          TaskViewElementEnum.Priority,
          ...frequentItems,
        ]),
      );
      break;
    case TasksKanbanGrouppingTypeEnum.Type:
      viewElements.value = Array.from(
        new Set([
          ...dataView,
          TaskViewElementEnum.Status,
          TaskViewElementEnum.Performers,
          TaskViewElementEnum.Priority,
          TaskViewElementEnum.Difficulty,
          TaskViewElementEnum.EstimateTime,
          TaskViewElementEnum.WorkTime,
        ]),
      );
      break;
    case TasksKanbanGrouppingTypeEnum.Author:
      viewElements.value = Array.from(
        new Set([
          ...dataView,
          TaskViewElementEnum.Status,
          TaskViewElementEnum.Priority,
          ...frequentItems,
        ]),
      );
      break;
  }
  loadAllGrouppedTasks(props.queueId);
}

/**При обновлении Id очереди */
async function onUpdateQueueId() {
  grouppingType.value =
    AttributesLocalStorageService.getGrouppingType(props.queueId, 'task-kanban') ??
    TasksKanbanGrouppingTypeEnum.Status;
  viewElements.value =
    AttributesLocalStorageService.getViewElements(props.queueId) ??
    Object.values(TaskViewElementEnum);
  loadAllGrouppedTasks(props.queueId);
  await checkProjectAdmin();
}
/**Проверить является ли пользователь админом проекта */
const checkProjectAdmin = async () => {
  if (CurrentUser.Roles.includes(UserRoleEnum.Owner)) {
    isProjectAdmin.value = true;
    return;
  }

  const projectId = Number(router.currentRoute.value?.params?.projectId);
  if (!projectId) return;

  const response = await middlewareService.CheckProjectAdmin(projectId);
  if (response.IsSuccess) isProjectAdmin.value = !!response.Value;
};
const sortTasks = (taskId: number, column: TasksKanbanColumnModel, orderNumber: number) => {};

const selectGrouppingType = async (_grouppingType: TasksKanbanGrouppingTypeEnum) => {
  isLoading.value = true;
  grouppingType.value = _grouppingType;
  AttributesLocalStorageService.saveGrouppingType(props.queueId, _grouppingType, 'task-kanban');
  debounceLoadAllGrouppedTasks(props.queueId);
};

/**Отправляемые данные в локал сторедж */
const denterDataLocalStorage = ref([]);
const updateViewElements = async (_viewElements: Array<TaskViewElementEnum>) => {
  viewElements.value = _viewElements;
  AttributesLocalStorageService.saveViewElements(props.queueId, _viewElements);
  debounceLoadAllGrouppedTasks(props.queueId);
};

/**Сброси поиска */
const resetSearch = async () => {
  resetSearchState.value = true;
  filter.value.Search = '';
  await loadAllGrouppedTasks(props.queueId);
  resetSearchState.value = false;
};

const {
  isDrag,
  currentDragTask,
  currentDragTaskColumnIndex,
  cleanFlag,
  onAdded,
  onChange,
  onDrag,
  onCancel,
} = useTasksKanbanDraggble(columns, {
  moveTask,
  sortTasks,
});
const sortParameters = async (params) => {
  columns.value.splice(0, columns.value.length);
  columns.value.push(...params.map((x) => x.Data));

  const param = params.map((p) => p.Data.Grouping.Status);

  await apiService.SetList(props.queueId, param);
};
// const isCanAddTask = ref(false);
/**Установка индекса перетаскиваемой колонки */
const setColumnDrag = (index?: number) => {
  allowDragColumnIndex.value = index;
};
/**Установка индекса перетаскиваемой колонки при перетаскивании задачи*/
const setDraggingColumnIndex = (index?: number) => {
  if (columnIndex.value === index) return;
  columnIndex.value = index;
};
/**скролл текущей колонки */
const scrollCurrentColumn = (index: number) => {
  return columnIndex.value === index && !!currentDragTask.value?.Data?.TaskId;
};

function openOptions() {
  emit('open-options', props.queueId);
}
function createTask() {
  openCreateTask();
}
/**Открыть окно удаления задачи */
const openRemoveModal = (id: number, name: string) => {
  $removeTaskModal.value?.open(id, name);
};
/**Удаление задачи из списка */
const onSuccessRemove = (taskId: number) => {
  columns.value.forEach((column) => {
    const haveTask = column.Data.some((t) => t.Data.TaskId == taskId);
    if (haveTask) {
      column.CountTasks--;
      column.Data = column.Data.filter((t) => t.Data.TaskId != taskId);
    }
  });

  loadAllGrouppedTasks(props.queueId);
};
</script>
<template>
  <div class="tasks-kanban">
    <div class="tasks-kanban__header">
      <TasksKanbanFilter
        :project="project"
        :projectId="projectId"
        :groupping-type="grouppingType"
        :view-elements="viewElements"
        :resetSearchState="resetSearchState"
        v-model:filter="filter"
        @update:groupping-type="selectGrouppingType"
        @update:view-elements="updateViewElements"
        @on-search="loadAllGrouppedTasks(queueId)"
        :isDisabled="!currentQueue?.IsConfigured"
      />

      <TirPmButton
        class="tasks-kanban__header__add"
        :variant="TirPmButtonVariantEnum.PrimaryState"
        :size="TirPmButtonSizeEnum.Small"
        @click="openCreateTask"
        :isDisabled="!currentQueue?.IsConfigured || !isCanAddTask"
      >
        <template #leftSlot><PlusIcon /></template>
      </TirPmButton>
    </div>
    <div
      class="tasks-kanban__main"
      v-if="tasks.length && columns.length && currentQueue.IsConfigured"
    >
      <div class="tasks-kanban__content">
        <div class="tasks-kanban__scroll">
          <KanbanLayout>
            <TirPmScrollbar style="max-width: calc(100vw - 12rem)">
              <TirPmDraggableSorting
                vertical
                skip-edge
                class="tasks-kanban__columns"
                :class="{
                  'tasks-kanban__columns--no-drag': nonDraggableGroups,
                }"
                :data="dragColumns"
                :indent="0"
                #item="slot"
                @change="sortParameters"
                style="margin-bottom: 0.5rem"
              >
                <KanbanColumn
                  :grouping="slot.item.Grouping"
                  :key="slot.index"
                  @mouseenter="setDraggingColumnIndex(slot.index)"
                >
                  <template #indicator>
                    <TasksKanbanColumnIndicator
                      :grouping="slot.item.Grouping"
                      @mouseenter="setColumnDrag(slot.index)"
                      @mouseleave="setColumnDrag(undefined)"
                    />
                  </template>
                  <template #count>
                    {{ slot.item.CountTasks }}
                  </template>
                  <template #tools>
                    <div class="tasks-kanban__tools">
                      <TirPmButton
                        :size="TirPmButtonSizeEnum.Small"
                        :variant="TirPmButtonVariantEnum.LinkState"
                      >
                        <template #leftSlot>
                          <EllipsisHorizontalIcon />
                        </template>
                      </TirPmButton>
                      <TirPmButton
                        :size="TirPmButtonSizeEnum.Small"
                        :variant="TirPmButtonVariantEnum.LinkState"
                        :isDisabled="!currentQueue?.IsConfigured || !isCanAddTask"
                        @click="openCreateTask(slot.item)"
                      >
                        <template #leftSlot>
                          <PlusIcon />
                        </template>
                      </TirPmButton>
                    </div>
                  </template>
                  <template #cards>
                    <TirPmScrollbar
                      showOnHover
                      :enableVerticalAutoscroll="scrollCurrentColumn(slot.index)"
                      class="tasks-kanban__column-scroll"
                      @on-scroll="onScrollColumn($event, slot.index)"
                    >
                      <div
                        class="tasks-kanban__task-column-list"
                        :style="{
                          height: !slot.item.Data?.length && currentDragTask ? '20rem' : 'auto',
                        }"
                      >
                        <KanbanDraggableSorting
                          dataClass="tasks-kanban__task-card-drag book-lesson"
                          :data="slot.item.Data"
                          :outerIsDrag="isDrag"
                          isAllowPlug
                          :disableSideIndent="true"
                          :outer-current-dragging="
                            currentDragTaskColumnIndex !== undefined &&
                            slot.index !== currentDragTaskColumnIndex
                              ? currentDragTask
                              : undefined
                          "
                          :clean-flag="cleanFlag"
                          :indent="0"
                          is-allow-click-delay
                          @dragComponentOnDrag="
                            (_isDrag: boolean, model: DragModel<{ TaskId: number }>) =>
                              onDrag(_isDrag, model, slot.index)
                          "
                          @added="onAdded(slot.index, $event)"
                          @change="onChange(slot.index, $event, slot.item.Data)"
                          @cancel="onCancel($event, slot.index)"
                          ><template #item="{ item }">
                            <TaskKanbanCard
                              :class="{ 'tasks-kanban__task-card--event-none': currentDragTask }"
                              v-if="tasksIndexed[item.TaskId]"
                              :task="tasksIndexed[item.TaskId]"
                              :userAccess="userAccess"
                              :view-settings="taskViewSettings"
                              :groupping-type="grouppingType"
                              :project-id="projectId"
                              :queue-id="queueId"
                              :isProjectAdmin="isProjectAdmin"
                              @toggle-submenu="emit('toggle-submenu', $event)"
                              @load-subtasks="loadSubtasks(item.TaskId)"
                              @open-in-drawer="openTaskInDrawer"
                              @remove="openRemoveModal"
                            />
                          </template>
                        </KanbanDraggableSorting>
                      </div>
                    </TirPmScrollbar>
                  </template>
                </KanbanColumn>
              </TirPmDraggableSorting>
            </TirPmScrollbar>
          </KanbanLayout>
        </div>
      </div>
    </div>
    <div class="is-no-correct" v-if="!isLoading && !currentQueue?.IsConfigured && currentQueue?.Id">
      <TirPmSystemMessage>
        <template #topDescImage>
          <div>
            <TirPmIconView style="width: 5rem; height: 5rem">
              <template #mainSlot>
                <ListBulletIcon
                  style="width: 3.5rem; height: 3.5rem; color: var(--neutral-70, #686c73)"
                />
              </template>
            </TirPmIconView>
          </div>
        </template>
        <template #mainTitleSlot
          >Настройте очередь, чтобы<br />
          создать в ней задачи
        </template>

        <template #descriptionSlot>Достаточно добавить 1 статус </template>
        <template #actionsSlot>
          <TirPmButton
            :variant="TirPmButtonVariantEnum.PrimaryState"
            :size="TirPmButtonSizeEnum.Regular"
            @click="openOptions"
            >Перейти к настройкам очереди
          </TirPmButton>
        </template>
      </TirPmSystemMessage>
    </div>
    <div
      class="is-no-correct"
      v-if="
        currentQueue?.IsConfigured && !countTasks && !isLoading && !filter.Search && !grouppingType
      "
    >
      <TirPmSystemMessage>
        <template #topDescImage>
          <div>
            <TirPmIconView style="width: 5rem; height: 5rem">
              <template #mainSlot>
                <DimondIcon
                  style="width: 3.5rem; height: 3.5rem; color: var(--neutral-70, #686c73)"
                />
              </template>
            </TirPmIconView>
          </div>
        </template>
        <template #mainTitleSlot>{{ createTaskPlaceholderText }}</template>
        <template #descriptionSlot></template>
        <template #actionsSlot>
          <TirPmButton
            v-if="isCanAddTask"
            :variant="TirPmButtonVariantEnum.PrimaryState"
            :size="TirPmButtonSizeEnum.Regular"
            @click="createTask"
            >Создать задачу
          </TirPmButton>
        </template>
      </TirPmSystemMessage>
    </div>
    <div
      v-if="(filter.Search && !countTasks) || (grouppingType && !countTasks)"
      class="tasks-kanban__no-find-block"
    >
      <TirPmIconView style="width: 5rem; height: 5rem">
        <template #mainSlot>
          <MagnifyingGlassIcon class="magnifying-glass-icon" />
        </template>
      </TirPmIconView>
      <p class="tasks-kanban__no-find-text">
        По вашему запросу ничего не <br />
        найдено
      </p>
      <TirPmButton
        v-if="filter.Search && !countTasks"
        :variant="TirPmButtonVariantEnum.PrimaryState"
        :size="TirPmButtonSizeEnum.Regular"
        @click="resetSearch"
      >
        Сбросить поиск
      </TirPmButton>
    </div>
    <div v-if="isLoading">
      <div class="tasks-kanban__skeleton-block">
        <TaskKanbanSkeleton />
        <TaskKanbanSkeleton />
        <TaskKanbanSkeleton />
        <TaskKanbanSkeleton />
      </div>
    </div>
    <TaskDrawer
      ref="$taskDrawer"
      @on-create-task="onCreateTask"
      @on-update-task="onUpdateTask"
      @subtaskItem="updateTask"
      @removeSubtaskItem="removeSubtaskItem"
      @successRemove="onSuccessRemove"
    />
    <RemoveTaskModal ref="$removeTaskModal" @successRemove="onSuccessRemove" />
  </div>
</template>
<style lang="scss">
.data.dragging {
  pointer-events: none;
}
.tasks-kanban {
  background-color: var(--neutral-0);
  box-shadow: 0rem 0rem 0.063rem 0rem rgba(0, 0, 0, 0.04),
    0rem 0.25rem 0.5rem 0rem rgba(0, 0, 0, 0.04);
  border-radius: 0.5rem;
  min-height: calc(100vh - 13rem);
  .is-no-correct {
    display: flex;
    justify-content: center;
    align-items: center;
    height: calc(100vh - 21rem);
  }
  &__task-card--event-none {
    pointer-events: none;
  }
  &__skeleton-block {
    display: flex;
    align-items: center;
  }
  &__header {
    display: flex;
    justify-content: space-between;
    column-gap: 1.25rem;
    padding: 1.5rem;
    &__add {
      position: sticky;
      right: 1rem;
    }
  }

  &__content {
    padding: 0 0.375rem;
  }

  &__task-column-list {
    z-index: 0;
    padding: 0.25rem 0.75rem 0.75rem 0.75rem;
    height: 100%;
  }
  &__scroll {
    padding: 0 1.125rem;
  }
  &__tools {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }
  &__column-scroll {
    max-height: calc(100vh - 23rem);
    width: 100%;
  }
  &__columns {
    .drag_wrapper {
      gap: 1.5rem;
      .data {
        height: 100%;
      }
    }
  }
  &__columns--no-drag {
    .drag_wrapper {
      cursor: auto !important;
    }
  }
}
</style>

<style lang="scss">
.tasks-kanban {
  &__no-find-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: calc(100vh - 21rem);
  }
  &__no-find-text {
    text-align: center;
    font-size: 1.6rem;
    font-weight: 500;
    margin: 0;
    margin-top: 1.5rem;
    margin-bottom: 2.5rem;
  }
  &__task-card-drag {
    z-index: 10;

    &.dragging {
      opacity: 0.8;
      cursor: grabbing;
      border-radius: 0.5rem;
      background: var(--bg-base, #fff);

      box-shadow: 0rem 1.25rem 1.5rem 0rem rgba(0, 0, 0, 0.08),
        0rem 0.75rem 1rem 0rem rgba(0, 0, 0, 0.04), 0rem 0.25rem 0.5rem 0rem rgba(0, 0, 0, 0.04),
        0rem 0rem 0.063rem 0rem rgba(0, 0, 0, 0.04);

      .task-kanban-card__subtasks {
        display: none;
      }
    }

    &:first-child {
      margin-top: 0 !important;
    }
    &:last-child {
      margin-bottom: 0 !important;
    }
  }
}

.magnifying-glass-icon {
  width: 2.8rem;
  height: 2.8rem;
}
.data.dragging {
  z-index: 99999;
}
.tasks-kanban__header__add {
  min-width: 2rem !important;
}
</style>
